import React, { useState, useEffect } from "react";
import "./Todo.css";

function TodoApp() {

  /* =========================
     STATES
  ========================= */

  const [tasks, setTasks] = useState(() => {
    const saved = localStorage.getItem("tasks");
    return saved ? JSON.parse(saved) : [];
  });

  const [input, setInput] = useState("");
  const [dateTime, setDateTime] = useState("");
  const [darkMode, setDarkMode] = useState(true);
  const [activeMenu, setActiveMenu] = useState("My Day");
  const [draggedTask, setDraggedTask] = useState(null);


  /* =========================
     SAVE TO LOCAL STORAGE
  ========================= */

  useEffect(() => {
    localStorage.setItem("tasks", JSON.stringify(tasks));
  }, [tasks]);

  /* =========================
     THEME SWITCH
  ========================= */

  useEffect(() => {
    document.body.className = darkMode ? "dark" : "light";
  }, [darkMode]);

  /* =========================
     ADD TASK
  ========================= */

  const addTask = () => {
    if (!input.trim()) return;

    const newTask = {
      id: Date.now(),
      text: input,
      completed: false,
      dateTime,
      important: false // ⭐ NEW
    };

    setTasks([...tasks, newTask]);
    setInput("");
    setDateTime("");
  };

  /* =========================
     COMPLETE TASK
  ========================= */

  const toggleComplete = (id) => {
    setTasks(tasks.map(task =>
      task.id === id
        ? { ...task, completed: !task.completed }
        : task
    ));
  };

  /* =========================
     ⭐ TOGGLE IMPORTANT
  ========================= */

  const toggleImportant = (id) => {
    setTasks(tasks.map(task =>
      task.id === id
        ? { ...task, important: !task.important }
        : task
    ));
  };
  /* =========================
   DRAG & DROP
========================= */

const handleDragStart = (task) => {
  setDraggedTask(task);
};

const handleDragOver = (e) => {
  e.preventDefault();
};

const handleDrop = (targetTask) => {
  if (!draggedTask || draggedTask.id === targetTask.id) return;

  const updatedTasks = [...tasks];

  const fromIndex = updatedTasks.findIndex(t => t.id === draggedTask.id);
  const toIndex = updatedTasks.findIndex(t => t.id === targetTask.id);

  const [movedTask] = updatedTasks.splice(fromIndex, 1);
  updatedTasks.splice(toIndex, 0, movedTask);

  setTasks(updatedTasks);
  setDraggedTask(null);
};


  /* =========================
     DELETE TASK (ANIMATED)
  ========================= */

  const deleteTask = (id) => {
    const element = document.getElementById(id);

    if (element) {
      element.style.animation = "taskExit 0.3s forwards";

      setTimeout(() => {
        setTasks(prev => prev.filter(task => task.id !== id));
      }, 300);
    }
  };

  /* =========================
     EDIT TASK
  ========================= */

  const editTask = (id) => {
    const task = tasks.find(t => t.id === id);
    const updatedText = prompt("Edit task:", task.text);

    if (!updatedText) return;

    setTasks(tasks.map(t =>
      t.id === id ? { ...t, text: updatedText } : t
    ));
  };

  /* =========================
     SIDEBAR FILTER LOGIC
  ========================= */

  const filteredTasks = tasks.filter(task => {
    if (activeMenu === "Important") return task.important;
    if (activeMenu === "Planned") return task.dateTime;
    if (activeMenu === "My Day") return true;
    return true; // All Tasks
  });

  /* =========================
     UI
  ========================= */

  return (
    <div className="app-layout">

      {/* ===== SIDEBAR ===== */}
      <div className="sidebar">
        <h2 className="logo">✔ Tasks</h2>

        <div className="menu">
          {["My Day", "Important", "Planned", "All Tasks"].map(item => (
            <div
              key={item}
              className={`menu-item ${
                activeMenu === item ? "active" : ""
              }`}
              onClick={() => setActiveMenu(item)}
            >
              {item}
            </div>
          ))}
        </div>
      </div>

      {/* ===== MAIN AREA ===== */}
      <div className="main-area">

        {/* HEADER */}
        <div className="header">
          <div>
            <h1>{activeMenu}</h1>
            <p>{new Date().toDateString()}</p>
          </div>

          <button
            className="theme-toggle"
            onClick={() => setDarkMode(!darkMode)}
          >
            {darkMode ? "☀️" : "🌙"}
          </button>
        </div>

        {/* TASK LIST */}
        <ul className="task-list">
          {filteredTasks.map(task => (
            <li
  key={task.id}
  id={task.id}
  draggable
  onDragStart={() => handleDragStart(task)}
  onDragOver={handleDragOver}
  onDrop={() => handleDrop(task)}
  className={`task-card
    ${task.completed ? "completed" : ""}
    ${task.important ? "important" : ""}
  `}
>

              <div className="task-left">
                <input
                  type="checkbox"
                  checked={task.completed}
                  onChange={() => toggleComplete(task.id)}
                />

                <div>
                  <span>{task.text}</span>

                  {task.dateTime && (
                    <div className="task-date">
                      {new Date(task.dateTime).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>

              <div className="task-actions">
                {/* ⭐ IMPORTANT BUTTON */}
                <button onClick={() => toggleImportant(task.id)}>
                  {task.important ? "⭐" : "☆"}
                </button>

                <button onClick={() => editTask(task.id)}>✏️</button>
                <button onClick={() => deleteTask(task.id)}>🗑</button>
              </div>
            </li>
          ))}
        </ul>

        {/* ===== INPUT BAR ===== */}
        <div className="task-input-bar">
          <input
            className="task-input"
            type="text"
            placeholder="Add a task..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
          />

          <input
            className="date-input"
            type="datetime-local"
            value={dateTime}
            onChange={(e) => setDateTime(e.target.value)}
          />

          <button className="add-btn" onClick={addTask}>
            Add
          </button>
        </div>

      </div>
    </div>
  );
}

export default TodoApp;
